import BedIcon from '@mui/icons-material/BedOutlined';
import PersonIcon from '@mui/icons-material/PeopleOutlined';
import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  GridLegacy as Grid,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';
// Avoid react-router Link here because this component can render without a Router
// (e.g. in Vike). Use a plain anchor and delegate navigation to our router hook.

import { fetchIcon } from '../../assets/icons/icons';
import LocationPin from '../../assets/icons/LocationPin';
import { useRouter } from '../../hooks/useRouter';
import {
  Boat,
  Experience,
  ExtraOptionType,
  ExtraOptionWithRelations,
  Property,
  useGetExtraOptionTypesQuery,
} from '../../redux/ellipsisApi';
import { theme } from '../../theme/theme';
import { getDisplayRate } from '../../utils/rateGetter';
import AspectImageLoader, { RenderedWidths } from '../ui/AspectImageLoader';

import FormatCurrency from './FormatCurrency';

const StyledTypography = styled(Typography)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const StyledOfferBox = styled(Box)(() => ({
  position: 'absolute',
  zIndex: '2',
  display: 'inline',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '1rem',
  top: '0.75rem',
  left: '0.75rem',
  padding: '0.5rem 0.75rem',
}));

const StyledOfferHeading = styled(Typography)(() => ({
  color: 'white',
  fontSize: '14px',
  fontWeight: 400,
}));

interface OwnProps {
  bookable?: Boat | Experience | Property;
  isFullPage?: boolean;
  cardHeading?: string;
  ownerCard?: boolean;
  bookableType: string;
  mixed?: boolean;
  editBookableCallback?: (id: string) => void;
  isBookableRecommended?: boolean;
  router?: ReturnType<typeof useRouter>;
  // Passed through to AspectImageLoader when the parent grid knows how wide
  // its cards render, e.g. the homepage.
  imageRenderedWidths?: RenderedWidths;
}

const ListingCard: React.FC<OwnProps> = (props) => {
  const {
    bookable,
    cardHeading,
    ownerCard = false,
    bookableType,
    mixed = false,
    editBookableCallback,
    isBookableRecommended,
    router,
  } = props;
  const { data: extraOptionTypes } = useGetExtraOptionTypesQuery();

  const href = `/${bookableType.toLowerCase()}/${bookable?.slug || bookable?.id}`;
  const onNavigate: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    // Intercept only unmodified left-clicks; let middle/right or meta clicks pass through.
    // Deliberately does NOT check e.defaultPrevented: Vike still ships a client-routing
    // runtime even with `clientRouting: false` (see src/pages/+Page.tsx), and that runtime
    // attaches its own capture-phase click listener that can call preventDefault() before
    // this handler runs. Gating on defaultPrevented let that leftover listener silently
    // take over navigation, racing this router and causing inconsistent soft/hard nav
    // (and the "Recommended" block getting stuck on its skeleton). Always driving the
    // navigation from here - preventDefault is idempotent - makes it deterministic.
    if (e.button === 0 && !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      router?.push(href);
    }
  };

  const getDefaultRate = useMemo(() => {
    const sessionType: string = bookable?.sessionType
      ? bookable?.sessionType
      : '';
    let bookingPeriod: string[] = [];

    switch (bookableType) {
      case 'Boat':
        bookingPeriod = ['full-day', 'half-day', 'overnight'];
        break;
      case 'Experience':
        bookingPeriod = ['per-child', 'per-adult', 'per-person', 'base'];
        break;
      case 'Property':
        bookingPeriod = ['overnight'];
        break;
      case 'Vehicle':
        bookingPeriod = ['full-day', 'half-day', 'overnight'];
        break;
    }

    return getDisplayRate(bookable?.rate ?? [], bookingPeriod, sessionType);
  }, [bookable?.rate, bookableType, bookable?.sessionType]);

  const roundedRating = useMemo(() => {
    return Math.round((bookable?.rating?.stars ?? 0) * 10) / 10;
  }, [bookable?.rating]);

  const withinDateRange = (startDate: string, endDate: string) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start <= today && today <= end;
  };

  const withSkipper = () => {
    const skipperExtraOptionTypeId = extraOptionTypes?.reduce(
      (optionTypeId: string | undefined, optionType: ExtraOptionType) => {
        if (optionType.name === 'Skipper') return optionType.id;
        return optionTypeId;
      },
      ''
    );
    const skipperOption = bookable?.extraOptions?.find(
      (option: ExtraOptionWithRelations) =>
        option.extraOptionTypeId === skipperExtraOptionTypeId
    );
    return skipperOption === undefined
      ? 'unavailable'
      : skipperOption.mandatory
        ? 'mandatory'
        : 'optional';
  };

  const renderSkipperStatus = () => {
    let title = '';
    switch (withSkipper()) {
      case 'optional':
        title = 'Bareboat Available';
        break;
      case 'mandatory':
        title = 'Crewed Only';
        break;
      case 'unavailable':
        title = 'Bareboat Only';
        break;
    }
    return (
      <Stack direction="column">
        <Stack direction="row" spacing={1} alignItems="center">
          {fetchIcon('captainsHatOutlined', 20, '#555')}
          <Stack direction="column">
            <Typography
              sx={{ fontWeight: 400, fontSize: '0.8rem', color: '#555' }}
            >
              {title}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const deactivatedBanner = useMemo(() => {
    if (bookable?.deactivated && bookable?.verified) {
      return (
        <Box
          sx={{
            position: 'absolute',
            width: `calc(100% - ${20}px )`,
            top: 10,
            left: 10,
          }}
        >
          <Alert severity="info" sx={{ alignItems: 'center' }}>
            Listing Deactivated
          </Alert>
        </Box>
      );
    }
  }, [bookable?.deactivated, bookable?.verified]);

  const verifiedBanner = useMemo(() => {
    if (!bookable?.verified) {
      return (
        <Box
          sx={{
            position: 'absolute',
            width: `calc(100% - ${20}px )`,
            top: 10,
            left: 10,
          }}
        >
          <Alert severity="info" sx={{ alignItems: 'center' }}>
            Awaiting admin approval
          </Alert>
        </Box>
      );
    }
  }, [bookable?.verified]);

  return (
    <Card
      sx={{
        cursor: 'pointer',
        borderRadius: '20px',
        flexShrink: 0,
        height: '100%',
      }}
      data-cy="listing-card"
    >
      <a
        href={href}
        onClick={onNavigate}
        // Vike still ships a client-routing runtime even with `clientRouting:
        // false` configured (see src/pages/+Page.tsx). That runtime's own
        // click listener (vike/dist/client/runtime-client-routing/
        // initOnLinkClick.js) intercepts every internal <a href> click
        // unconditionally - it never checks e.defaultPrevented - and runs
        // its own competing page transition in parallel with this router's.
        // `data-vike="false"` is Vike's own documented opt-out
        // (isLinkIgnored) for links a different router already owns; without
        // it the two transitions raced, which is what left the
        // "Recommended" section stuck on its skeleton after a completed,
        // successful fetch (EM-1177/EM-1178).
        data-vike="false"
        style={{
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {cardHeading && (
          <CardContent>
            <Typography variant="h2" align="center" data-cy="card-title">
              {cardHeading}
            </Typography>
          </CardContent>
        )}

        <Box position="relative" style={{ flexGrow: 0, overflow: 'hidden' }}>
          {bookable?.mediaData?.length > 0 && (
            <AspectImageLoader
              src={bookable?.mediaData[0].path}
              renderedWidths={props.imageRenderedWidths}
              aspect={'4:3'}
              isSliced={false}
              backgroundPosition={'center top'}
            />
          )}
          {!ownerCard &&
            bookable?.rate?.some(
              (rate) =>
                rate.validForever !== true &&
                rate.validFrom &&
                rate.validTo &&
                withinDateRange(rate.validFrom, rate.validTo) &&
                !rate.userIds?.length
            ) && (
              <StyledOfferBox>
                <StyledOfferHeading>Discount offer</StyledOfferHeading>
              </StyledOfferBox>
            )}
          {!ownerCard && bookable?.noLicenceRequired && (
            <StyledOfferBox>
              <StyledOfferHeading>No Licence Required</StyledOfferHeading>
            </StyledOfferBox>
          )}
          {deactivatedBanner}
          {verifiedBanner}
        </Box>
        <CardContent sx={{ position: 'relative', flexGrow: 1 }}>
          <Grid
            container
            spacing={3}
            sx={{
              height: '100%',
              marginTop: 0,
              position: 'relative',
              paddingBottom: `${isBookableRecommended ? '52px' : '40px'}`,
            }}
          >
            <Grid item xs={10} sx={{ flexGrow: 0 }}>
              {bookable?.address || bookable?.port ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationPin sx={{ color: '#555555' }} />
                  <StyledTypography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      lineHeight: '1.2',
                      color: '#555',
                    }}
                    data-cy="listing-location"
                  >
                    {bookableType === 'Boat'
                      ? bookable?.port?.name
                      : bookable?.address}
                  </StyledTypography>
                </Stack>
              ) : (
                <div style={{ height: '1.2rem' }} />
              )}
              {bookableType === 'Boat' ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon sx={{ color: '#555555', height: '20px' }} />
                  <StyledTypography
                    sx={{
                      fontWeight: 400,
                      fontSize: '0.8rem',
                      color: '#555',
                    }}
                  >
                    {bookable?.capacity}
                  </StyledTypography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: '1.2rem' }}
                  />
                  {renderSkipperStatus()}
                </Stack>
              ) : bookableType === 'Property' ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonIcon sx={{ color: '#555555', height: '20px' }} />
                  <StyledTypography
                    sx={{
                      fontWeight: 400,
                      fontSize: '0.8rem',
                      color: '#555',
                    }}
                  >
                    {bookable?.numberOfRooms}
                  </StyledTypography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ height: '1.2rem' }}
                  />
                  <BedIcon sx={{ color: '#555555', height: '20px' }} />
                  <StyledTypography
                    sx={{
                      fontWeight: 400,
                      fontSize: '0.8rem',
                      color: '#555',
                    }}
                  >
                    {bookable?.capacity}
                  </StyledTypography>
                </Stack>
              ) : (
                mixed && <Box height={'15px'} />
              )}
            </Grid>
            {bookable?.rating && (
              <Grid item xs={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={'flex-end'}
                >
                  <StarIcon sx={{ color: '#14b57b' }} />
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                    }}
                  >
                    {roundedRating}
                  </Typography>
                </Stack>
              </Grid>
            )}
            <Grid item xs={12} sx={{ flexGrow: 1 }}>
              <StyledTypography
                variant="h3"
                data-cy="listing-name"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  lineHeight: '1.2',
                  color: theme.palette.primary.main,
                  marginBottom: '1.3rem',
                }}
              >
                {bookable?.name}
              </StyledTypography>
              <FormatCurrency
                variant={'h2'}
                data-cy={'listing-price'}
                price={getDefaultRate.price}
                currencyCode={bookable?.currencyCode ?? 'eur'}
                preText={'from'}
                postText={getDefaultRate.postText}
                priceOnRequest={bookable?.priceOnRequest}
                sx={{
                  fontWeight: 400,
                  fontSize: '0.8rem',
                  lineHeight: '1.2',
                  color: '#555',
                  paddingTop: '2px',
                }}
              />
            </Grid>
            {!ownerCard ? (
              <Grid
                className="button-wrapper"
                item
                xs={12}
                sx={{ flexGrow: 0, padding: 0, height: 0 }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    width: 'calc(100% - 12px)',
                  }}
                >
                  <Button
                    variant="outlined"
                    data-cy="listing-view-btn"
                    fullWidth
                    sx={{
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                      border: '1.5px solid',
                      borderRadius: '20px',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: '1.2',
                      textTransform: 'none',
                      padding: '10px 0',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                      },
                    }}
                  >
                    View&nbsp;{bookableType}
                  </Button>
                </Box>
              </Grid>
            ) : (
              editBookableCallback && (
                <Grid
                  className="button-wrapper"
                  item
                  xs={12}
                  sx={{ flexGrow: 0, padding: 0, height: 0 }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      width: 'calc(100% - 12px)',
                    }}
                  >
                    <Button
                      sx={{
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        lineHeight: '1.2',
                        textTransform: 'none',
                        padding: '10px 0',
                      }}
                      fullWidth
                      variant="contained"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        editBookableCallback(bookable?.id ?? '');
                      }}
                      data-cy="listing-edit-btn"
                    >
                      Edit&nbsp;{bookableType}
                    </Button>
                  </Box>
                </Grid>
              )
            )}
          </Grid>
        </CardContent>
      </a>
    </Card>
  );
};

export default ListingCard;
